import React, { useState, useEffect, useCallback, Fragment } from 'react'

import { Dropdown } from '@components/common/form'
import { FlexContainer } from '@components/common'

import { SchemaDriftResponse } from '@types'

const OBJECT_TYPES = [
  {
    label: 'Food Partners',
    value: 'food_partners',
  },
]

interface SchemaManagerProps {
  checkSchemaDrift: (objectType: string) => Promise<SchemaDriftResponse | null>
}

const SchemaManager: React.FC<SchemaManagerProps> = ({ checkSchemaDrift }) => {
  const [objectType, setObjectType] = useState<string | null>(null)
  const [driftSummary, setDriftSummary] = useState<SchemaDriftResponse | null>(
    null,
  )

  const checkDrift = useCallback(async () => {
    if (!objectType) {
      setDriftSummary(null)

      return
    }

    const driftSummary = await checkSchemaDrift(objectType)
    if (driftSummary) {
      setDriftSummary(driftSummary)
    }
  }, [objectType, checkSchemaDrift])

  useEffect(() => {
    checkDrift()
  }, [checkDrift])

  return (
    <FlexContainer flexDirection="row" width="100%" padding="10px">
      <div className="w-1/5">
        <Dropdown
          label="HubSpot Object Type"
          value={objectType}
          onChange={(e) => setObjectType(e.target.value)}
        >
          <option />
          {OBJECT_TYPES.map(({ label, value }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Dropdown>
      </div>
      <div className="w-4/5 px-6">
        {driftSummary && (
          <Fragment>
            <div
              className={`mb-4 p-3 rounded text-sm font-medium ${
                driftSummary.hasDrift
                  ? 'bg-yellow-50 text-yellow-800 border border-yellow-300'
                  : 'bg-green-50 text-green-800 border border-green-300'
              }`}
            >
              {driftSummary.summary}
            </div>

            {driftSummary.missing.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-semibold text-red-700 mb-1">
                  Missing Properties ({driftSummary.missing.length})
                </p>
                <table className="w-full text-sm border border-gray-200 rounded">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">
                        Name
                      </th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">
                        Label
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {driftSummary.missing.map((p) => (
                      <tr key={p.name} className="border-t border-gray-100">
                        <td className="px-3 py-2 font-mono text-xs text-gray-700">
                          {p.name}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{p.label}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {driftSummary.optionDiffs.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-semibold text-orange-700 mb-1">
                  Properties with Missing Options (
                  {driftSummary.optionDiffs.length})
                </p>
                <table className="w-full text-sm border border-gray-200 rounded">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">
                        Name
                      </th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">
                        Label
                      </th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">
                        Missing Options
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {driftSummary.optionDiffs.map((p) => (
                      <tr key={p.name} className="border-t border-gray-100">
                        <td className="px-3 py-2 font-mono text-xs text-gray-700">
                          {p.name}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{p.label}</td>
                        <td className="px-3 py-2 text-gray-700">
                          {p.missingOptions.join(', ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {driftSummary.extra.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-semibold text-gray-600 mb-1">
                  Extra Properties (live only, {driftSummary.extra.length})
                </p>
                <table className="w-full text-sm border border-gray-200 rounded">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">
                        Name
                      </th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">
                        Label
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {driftSummary.extra.map((p) => (
                      <tr key={p.name} className="border-t border-gray-100">
                        <td className="px-3 py-2 font-mono text-xs text-gray-700">
                          {p.name}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{p.label}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Fragment>
        )}
      </div>
    </FlexContainer>
  )
}

export default SchemaManager
